import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { isNonEmptyArray, isNonEmptyObject, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import PropTypes from "prop-types";
import { v4 as uuidv4 } from "uuid";

import Card from "components/Card";
import { ADMINISTRATION_API_ROUTE, FORM_SUBMISSION_TYPES } from "globals/constants";
import { useDictionary } from "hooks";
import { awardSelectors, proposalSelectors } from "store/selectors";
import { spUrl, sortCollectionByKey } from "utils";
import { executeRequest } from "utils/proxy";


import styles from "./styles.css";

const useFormData = formType => {
	const proposalContent = useSelector(proposalSelectors.proposal);
	const awardContent = useSelector(awardSelectors.award);
	const { data: proposalValues } = useSelector(proposalSelectors.values);
	const { data: awardValues } = useSelector(awardSelectors.values);

	if(formType === FORM_SUBMISSION_TYPES.PROPOSAL) {
		return { formContent: proposalContent, formValues: proposalValues };
	}

	if(formType === FORM_SUBMISSION_TYPES.AWARD) {
		return { formContent: awardContent, formValues: awardValues };
	}

	return {};
};

export const ExternalRecords = ({ formType }) => {
	formType = formType.toLowerCase();

	const dictionary = useDictionary("common.externalRecords");
	const { formContent, formValues } = useFormData(formType);
	const formId = (formValues ?? {})[`${formType}Id`];
	const hasS2SLink = isNonEmptyString(formContent?.s2sReferenceId);

	const [ externalLinks, setExternalLinks ] = useState({});

	const renderKeyValues = attributes => {
		const links = [];
		const nonLinks = [];

		attributes.forEach(attribute => {
			attribute.link ? links.push(attribute.link) : nonLinks.push(attribute);
		});

		const sortedLinks = sortCollectionByKey(links, "label");
		const sortedNonLinks = sortCollectionByKey(nonLinks, "label");

		return (
			<>
				{
					sortedLinks.map(link => (
						<p key={link.label}>
							<strong>{link.label}:&nbsp;</strong>
							{ link.shouldLink ? (
								<a key={uuidv4()} href={link.url} rel="noopener noreferrer" target="_blank">
									{link.value}
								</a>
							) : link.url }
						</p>
					))}
				{
					sortedNonLinks.map(attribute => (
						<p key={attribute.label}><strong>{attribute.label}:</strong> {attribute.value}</p>
					))
				}
			</>
		);
	};

	const groupDataBySystemName = sortedExternalLinks => {
		const groupedData = {};
		sortedExternalLinks.forEach(link => {
			if(groupedData[link.systemName]) {
				groupedData[link.systemName].push(link);
			} else {
				groupedData[link.systemName] = [ link ];
			}
		});

		return groupedData;
	};

	const s2sDataToLink = s2sServiceData => {
		const {
			s2sPiFullName,
			s2sProposalTitle,
			s2sProposalUrl,
			submissionStatus = "Not Available"
		} = s2sServiceData;

		const statusAttribute = {
			key: "status",
			label: "Status",
			value: submissionStatus
		};
		const s2sLinkAttribute = {
			label: "Proposal(S2S) Record",
			link: {
				label: "Proposal(S2S) Record",
				value: `${s2sProposalTitle} - ${s2sPiFullName}`,
				url: s2sProposalUrl,
				shouldLink: true
			},
		};

		return {
			systemKey: "proposals-s2s-record",
			systemName: "Proposals(S2S)",
			systemAttributes: [
				statusAttribute,
				s2sLinkAttribute
			]
		};
	};

	useEffect(() => {
		if (formId && formType) {
			const s2sIntegrationServiceUrl = spUrl(`/sp/api/integration/S2S/${formId}`);
			const serviceRequests = [
				executeRequest(`${ADMINISTRATION_API_ROUTE}/${formType}-links?search=${formType}Id:${formId}`),
				hasS2SLink ? executeRequest(s2sIntegrationServiceUrl) : null
			];

			Promise.all(serviceRequests)
				.then(data => {
					const [ externalLinksServiceData, s2sServiceData ] = data;
					const links = [];

					if (externalLinksServiceData._embedded) {
						links.push(externalLinksServiceData._embedded[`${formType}-links`]);
					}

					if (s2sServiceData && isNonEmptyObject(s2sServiceData.s2sLink)) {
						links.push([ s2sDataToLink(s2sServiceData.s2sLink) ]);
					}

					if (isNonEmptyArray(links)) {
						let groupedSystems = {};
						links.forEach(link => {
							const sortedExternalLinks = sortCollectionByKey(link, "systemName");
							const groupedLinks = groupDataBySystemName(sortedExternalLinks);
							groupedSystems = { ...groupedSystems, ...groupedLinks };
						});
						setExternalLinks(groupedSystems);
					}
				});
		}
	}, [ formId, formType, hasS2SLink ]);

	return (
		<div className={styles.container}>
			<div className={styles.headerSegment}><span>{dictionary.title ?? "External Records"}</span></div>
			<div className={styles.content}>
				{isNonEmptyObject(externalLinks) ?
					<div className={styles.externalLinksContainer}>
						{
							isNonEmptyObject(externalLinks) &&
						Object.keys(externalLinks).map(key => (
							<div className={styles.cardGroup} key={uuidv4()}>
								{
									externalLinks[key].map((link, index) => (
										<Card key={`${link.systemName}-${index}`} title={link.systemName} className={styles.card}>
											{renderKeyValues(link.systemAttributes)}
										</Card>
									))
								}
							</div>
						))
						}
						{/* Extra Card if odd number of cards */}
						{externalLinks.length * 2 !== 0 && <div key="dummyCard" className={styles.cardGroup} />}
					</div> :
					<div className={styles.noLinks}>{ dictionary.noRecords || "No External Records have been added yet."}</div>
				}
			</div>
		</div>
	);
};

ExternalRecords.propTypes = {
	formType: PropTypes.string.isRequired
};

export default ExternalRecords;
